/**
 * Reach + Media module contracts (request/response shapes only, no business logic).
 * Routes live in apps/api/src/routes/mod-reach-media*.ts under /api/v1 (see RM_ROUTES below).
 * Every derived value (WhatsApp eligibility, counts, guard verdicts, story time-outs, path status)
 * is computed by the server; the client never sends it.
 */
import { z } from 'zod';

const Id = z.string().uuid();
const IsoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD (IST calendar date)');
const VKey = z.string().regex(/^[a-z][a-z0-9_-]{1,31}$/);
const txt = (max: number) => z.string().trim().max(max);

// ------------------------------------------------------------------ Reach: people (rolodex)
export const RmTag = z.enum(['professional', 'hcp', 'personal']);
export type RmTag = z.infer<typeof RmTag>;

export const RmCrossArea = z.object({
  verticalName: z.string(),
  relationshipId: Id,
  lastContact: IsoDate.nullable(),
  /** e.g. "Education contacted this person 2 days ago" (computed on the server, IST). */
  label: z.string(),
});

export const RmPerson = z.object({
  id: Id,
  name: z.string(),
  company: z.string(),
  designation: z.string(),
  func: z.string(),
  phones: z.array(z.string()),
  emails: z.array(z.string()),
  source: z.string(),
  tag: RmTag.nullable(),
  services: z.array(z.string()),
  excluded: z.boolean(),
  excludedReason: z.string(),
  linkedinUrl: z.string(),
  connectedOn: z.string(),
  linkedinNote: z.string(),
  confirmedOn: IsoDate.nullable(),
  contactId: Id.nullable(),
  /** Server-normalised WhatsApp-ready numbers (+91…), in order. Empty = no usable mobile. */
  whatsapp: z.array(z.string()),
  version: z.number().int().nonnegative(),
});
export type RmPerson = z.infer<typeof RmPerson>;

export const RmPersonDetail = RmPerson.extend({ crossArea: z.array(RmCrossArea), inPipelineAs: z.array(z.string()) });
export type RmPersonDetail = z.infer<typeof RmPersonDetail>;

const csv = z.union([z.array(z.string()), z.string()]).optional()
  .transform((v) => (v === undefined ? [] : Array.isArray(v) ? v : v.split('|').filter(Boolean)));
export const RmChannel = z.enum(['wa', 'em', 'both']);
export const RmSource = z.enum(['phonebook', 'linkedin', 'merged', 'manual', 'manual-icp', 'li-desk']);
export const RmTagFilter = z.enum(['professional', 'hcp', 'personal', 'notpersonal', 'untagged']);

/** Filters shared by the list, the export and saved lists (audiences). */
export const RmFilters = z.object({
  q: txt(200).optional(),
  companies: z.array(txt(300)).max(500).default([]),
  designations: z.array(txt(300)).max(500).default([]),
  channel: RmChannel.optional(),
  source: RmSource.optional(),
  tag: RmTagFilter.optional(),
  /** Service name, or "__none" for people with no service interest. */
  service: txt(120).optional(),
  /** Leave out everyone already in the pipeline (default true: live relationships are never campaigned at). */
  excludePipeline: z.boolean().default(true),
  excluded: z.enum(['hide', 'only', 'all']).default('all'),
});
export type RmFilters = z.infer<typeof RmFilters>;

export const RmPeopleQuery = z.object({
  q: txt(200).optional(),
  companies: csv,
  designations: csv,
  channel: RmChannel.optional(),
  source: RmSource.optional(),
  tag: RmTagFilter.optional(),
  service: txt(120).optional(),
  excludePipeline: z.enum(['true', 'false']).optional().transform((v) => v === 'true'),
  excluded: z.enum(['hide', 'only', 'all']).default('all'),
  limit: z.coerce.number().int().min(1).max(200).default(200),
  cursor: z.string().optional(),
});

export const RmPeopleSummary = z.object({
  total: z.number().int(),
  match: z.number().int(),
  waEligible: z.number().int(),
  uniqueNumbers: z.number().int(),
  withEmail: z.number().int(),
  excludedTotal: z.number().int(),
  untaggedWithWhatsapp: z.number().int(),
});
export const RmPeoplePage = z.object({ items: z.array(RmPerson), nextCursor: z.string().nullable(), summary: RmPeopleSummary });
export type RmPeoplePage = z.infer<typeof RmPeoplePage>;

export const RmFacets = z.object({
  companies: z.array(z.object({ value: z.string(), count: z.number().int() })),
  designations: z.array(z.object({ value: z.string(), count: z.number().int() })),
  services: z.array(z.string()),
});

export const RmCreatePerson = z.object({
  name: txt(300).min(1),
  company: txt(300).default(''),
  designation: txt(300).default(''),
  func: txt(120).default(''),
  phones: z.array(txt(40)).max(10).default([]),
  emails: z.array(txt(200)).max(10).default([]),
  tag: RmTag.nullable().default(null),
  services: z.array(txt(120)).max(20).default([]),
  confirmNotDuplicate: z.boolean().default(false),
}).strict();

export const RmUpdatePerson = z.object({
  name: txt(300).min(1).optional(),
  company: txt(300).optional(),
  designation: txt(300).optional(),
  func: txt(120).optional(),
  phones: z.array(txt(40)).max(10).optional(),
  emails: z.array(txt(200)).max(10).optional(),
  tag: RmTag.nullable().optional(),
  services: z.array(txt(120)).max(20).optional(),
  excluded: z.boolean().optional(),
  contactId: Id.nullable().optional(),
  version: z.number().int().nonnegative(),
}).strict();

export const RmDuplicate = z.object({ person: RmPerson, matchedOn: z.array(z.enum(['phone', 'email', 'name'])) });

// imports
export const RmImportText = z.object({ text: z.string().min(1).max(900_000), dryRun: z.boolean().default(false) }).strict();
export const RmPhonebookResult = z.object({
  dryRun: z.boolean(), read: z.number().int(), added: z.number().int(), merged: z.number().int(), junk: z.number().int(),
  sample: z.array(z.object({ name: z.string(), phones: z.array(z.string()), emails: z.array(z.string()), action: z.enum(['add', 'merge', 'junk']) })),
});
export const RmLinkedInRow = z.object({
  name: txt(300).min(1), company: txt(300).default(''), position: txt(300).default(''),
  email: txt(200).default(''), url: txt(500).default(''), connectedOn: txt(40).default(''),
});
export type RmLinkedInRow = z.infer<typeof RmLinkedInRow>;
export const RmLinkedInResult = z.object({
  dryRun: z.boolean(), read: z.number().int(), matched: z.number().int(), keptOwnerTruth: z.number().int(),
  unmatched: z.array(RmLinkedInRow),
});
export const RmAddRows = z.object({ rows: z.array(RmLinkedInRow).min(1).max(2000) }).strict();
export const RmFuzzyRequest = z.object({ rows: z.array(RmLinkedInRow).min(1).max(150) }).strict();
export const RmFuzzyProposal = z.object({ personId: Id, personName: z.string(), linkedin: RmLinkedInRow, confidence: z.enum(['high', 'medium']) });
export const RmFuzzyResponse = z.object({ proposals: z.array(RmFuzzyProposal).nullable() });
export const RmApplyMatches = z.object({ matches: z.array(z.object({ personId: Id, linkedin: RmLinkedInRow })).min(1).max(150) }).strict();
export const RmTagFileRequest = z.object({ text: z.string().min(1).max(900_000) }).strict();
export const RmIcpRequest = z.object({ label: txt(120).min(1), service: txt(120).optional(), numbers: z.string().min(1).max(50_000) }).strict();
export const RmIcpResult = z.object({ added: z.number().int(), duplicates: z.number().int(), invalid: z.number().int(), invalidSamples: z.array(z.string()) });

// tools
export const RmCountResult = z.object({ message: z.string(), counts: z.record(z.number().int()) });
export const RmSweepRequest = z.object({ contains: z.array(txt(40)).max(100).default([]), starts: z.array(txt(40)).max(100).default([]), emailContains: z.array(txt(80)).max(100).default([]) }).strict();
export const RmSweepResult = z.object({ swept: z.number().int(), byRule: z.array(z.object({ rule: z.string(), count: z.number().int() })) });
export const RmAnalysis = z.object({
  phoneOnly: z.number().int(), linkedinOnly: z.number().int(), joined: z.number().int(),
  exactOverlap: z.number().int(), firstNameHits: z.number().int(),
  phoneSamples: z.array(z.string()), linkedinSamples: z.array(z.string()),
});
export const RmAudit = z.object({
  cards: z.number().int(), withNumber: z.number().int(), noNumber: z.number().int(),
  eligibleCards: z.number().int(), uniqueEligible: z.number().int(), sharedNumbers: z.number().int(), rejectedCards: z.number().int(),
  reasons: z.array(z.object({ key: z.string(), label: z.string(), count: z.number().int(), samples: z.array(z.string()) })),
  eligibleIndia: z.number().int(), eligibleIntl: z.number().int(),
});
export const RmCheckResult = z.object({
  pipeline: z.array(z.object({ relationshipId: Id, name: z.string(), area: z.string() })),
  people: z.array(z.object({ person: RmPerson, status: z.string(), inList: z.boolean() })),
  more: z.number().int(),
});
export const RmExportRequest = z.object({ kind: z.enum(['csv', 'linkedin', 'whatsapp']), filters: RmFilters }).strict();
export const RmExportResult = z.object({
  fileName: z.string(), content: z.string(), rows: z.number().int(),
  notes: z.array(z.string()),
});
export const RmSettings = z.object({
  senderAddress: z.string(),
  excludeRules: z.object({ contains: z.array(z.string()), starts: z.array(z.string()), emailContains: z.array(z.string()) }),
});
export const RmUpdateSettings = z.object({ senderAddress: txt(2000).optional(), excludeRules: RmSweepRequest.optional() }).strict();

// audiences (saved lists)
export const RmAudience = z.object({ id: Id, name: z.string(), filters: RmFilters, matchCount: z.number().int(), updatedAt: z.string() });
export type RmAudience = z.infer<typeof RmAudience>;
export const RmSaveAudience = z.object({ name: txt(120).min(1), filters: RmFilters }).strict();

// ------------------------------------------------------------------ Reach: target accounts
export const RmRoute = z.enum(['warm', 'referral', 'cold']);
export const RmPathStatus = z.enum(['open', 'in_motion', 'silence_hold', 'exhausted']);
export const RmOfficial = z.object({
  name: txt(200).default(''), role: txt(200).default(''), phone: txt(80).default(''),
  email: txt(200).default(''), source: txt(80).default('added by hand'), remark: txt(300).default(''),
});
export const RmPath = z.object({
  id: Id, personName: z.string(), route: RmRoute, status: RmPathStatus, linkKind: z.enum(['register', 'rolodex', 'free']),
  relationshipId: Id.nullable(), note: z.string(),
  /** Plain words, computed on the server from the linked pipeline person when there is one. */
  statusLabel: z.string(), live: z.boolean(),
});
export const RmTarget = z.object({
  id: Id, verticalKey: z.string(), company: z.string(), city: z.string(), address: z.string(), phones: z.string(),
  setName: z.string(), rank: z.number().int().nullable(), officials: z.array(RmOfficial), enrichedOn: IsoDate.nullable(),
  paths: z.array(RmPath),
  glance: z.object({ label: z.string(), tone: z.enum(['success', 'warning', 'neutral']) }),
  version: z.number().int().nonnegative(),
});
export type RmTarget = z.infer<typeof RmTarget>;
export const RmTargetsQuery = z.object({ vertical: VKey, set: txt(80).optional() });
export const RmTargetsResponse = z.object({
  items: z.array(RmTarget),
  sets: z.array(z.object({ name: z.string(), members: z.number().int(), inList: z.number().int(), withPath: z.number().int(), seedable: z.boolean() })),
  coverage: z.object({ withPath: z.number().int(), members: z.number().int(), warm: z.number().int(), referral: z.number().int(), cold: z.number().int(), holdOrExhausted: z.number().int(), noPath: z.array(z.string()) }),
});
export const RmCreateTarget = z.object({ vertical: VKey, company: txt(300).min(1), city: txt(120).default(''), address: txt(1000).default(''), phones: txt(300).default(''), setName: txt(80).default('') }).strict();
export const RmUpdateTarget = z.object({ company: txt(300).min(1).optional(), city: txt(120).optional(), address: txt(1000).optional(), phones: txt(300).optional(), setName: txt(80).optional(), version: z.number().int().nonnegative() }).strict();
export const RmSeedTargets = z.object({ vertical: VKey, set: z.enum(['IPM Top 20', 'IPM 21-100']) }).strict();
export const RmImportTargets = z.object({
  vertical: VKey, fileName: txt(300).min(1),
  /** CSV text, or an .xlsx file as base64 (read on the server; nothing is stored). */
  text: z.string().max(900_000).optional(), base64: z.string().max(900_000).optional(),
}).strict().refine((v) => !!v.text || !!v.base64, 'give the file');
export const RmImportTargetsResult = z.object({ created: z.number().int(), officialsAdded: z.number().int(), skipped: z.number().int() });
export const RmAddOfficial = RmOfficial.extend({ name: txt(200).min(1) }).strict();
export const RmAddPath = z.object({ personName: txt(300).min(1), route: RmRoute.default('warm'), status: RmPathStatus.default('open'), note: txt(1000).default(''), relationshipId: Id.optional() }).strict();
export const RmUpdatePath = z.object({ route: RmRoute.optional(), status: RmPathStatus.optional(), note: txt(1000).optional(), relationshipId: Id.nullable().optional() }).strict();
export const RmProposedPath = z.object({
  person: z.string(), route: RmRoute, evidence: z.string(), line: z.string(),
  linkKind: z.enum(['register', 'rolodex', 'official', 'none']), relationshipId: Id.nullable(), alreadySaved: z.boolean(),
});
export const RmProposePathsResponse = z.object({ paths: z.array(RmProposedPath).nullable(), noOwnedPath: z.boolean() });
export const RmLabelsRequest = z.object({ vertical: VKey, role: txt(120).min(1), product: txt(200).default(''), senderAddress: txt(2000).default('') }).strict();
export const RmLabelsResponse = z.object({ text: z.string(), csv: z.string(), count: z.number().int(), named: z.number().int(), preview: z.array(z.string()) });
export const RmMailer = z.object({ id: Id, sentOn: IsoDate, product: z.string(), role: z.string(), labelCount: z.number().int(), namedCount: z.number().int() });
export const RmRecordMailer = z.object({ vertical: VKey, role: txt(120).min(1), product: txt(200).default('') }).strict();

// ------------------------------------------------------------------ Reach: LinkedIn desk
export const RmLiStage = z.enum(['commented', 'connection_sent', 'connected', 'dm_sent', 'replied', 'silent', 'nurturing']);
export type RmLiStage = z.infer<typeof RmLiStage>;
export const RmLiSections = z.object({
  post: z.string(), comment: z.string(), note: z.string(), dm: z.string(), rules: z.string(),
  readerComment: z.string(), publicReply: z.string(), engagement: z.string(),
});
export const RmLiParsed = z.object({
  author: z.string(), url: z.string(), captured: z.string(), fitLine: z.string(), fitService: z.string(), noFit: z.boolean(),
  direction: z.enum(['inbound', 'outbound']), sections: RmLiSections,
});
export const RmLiParseResponse = z.object({
  parsed: RmLiParsed, knownAuthor: z.boolean(), knownPost: z.boolean(), alreadyConnected: z.boolean(),
  /** Set when the author was already sent a nudge message (never nudge twice). */
  nudgedOn: IsoDate.nullable(), carries: z.array(z.string()), missing: z.array(z.string()),
});
export const RmLiSave = z.object({
  text: z.string().min(1).max(60_000),
  /** "" keep as read, "__none" no genuine fit, else a service name. */
  fit: txt(120).default(''),
  engagedOn: IsoDate.optional(),
  alreadyConnected: z.boolean().default(false),
}).strict();
export const RmLiEngagement = z.object({
  id: Id, url: z.string(), fitLine: z.string(), fitService: z.string(), noFit: z.boolean(), direction: z.enum(['inbound', 'outbound']),
  sections: RmLiSections, stage: RmLiStage, stages: z.record(z.string()), connClosed: z.boolean(), threadClosed: z.boolean(),
  nurtureOn: IsoDate.nullable(), deskNotes: z.string(), dmText: z.string(), dmService: z.string(), overrides: z.array(z.string()),
  /** Server-computed: what the desk suggests next, in plain words, and whether it is due today. */
  nextStep: z.string(), due: z.boolean(), daysInStage: z.number().int(),
});
export const RmLiAuthor = z.object({
  id: Id, name: z.string(), profileUrl: z.string(), relationshipId: Id.nullable(),
  reentryGrant: z.object({ service: z.string(), grantedOn: z.string(), used: z.boolean() }).nullable(),
  nudgedOn: IsoDate.nullable(), engagements: z.array(RmLiEngagement),
});
export type RmLiAuthor = z.infer<typeof RmLiAuthor>;
export const RmLiStageRequest = z.object({ stage: RmLiStage }).strict();
export const RmLiNote = z.object({ text: txt(500).min(1) }).strict();
export const RmLiDmDraft = z.object({ instruction: txt(500).optional(), current: txt(2000).optional(), serviceUrl: txt(500).optional() }).strict();
export const RmLiDmDraftResponse = z.object({ dm: z.string().nullable(), problems: z.array(z.string()) });
export const RmLiDmSave = z.object({ text: txt(2000).min(1) }).strict();
export const RmLiDmSaveResponse = z.object({ saved: z.boolean(), problems: z.array(z.string()) });
export const RmLiGrant = z.object({ service: txt(120).min(1) }).strict();
export const RmLiLink = z.object({ relationshipId: Id.nullable() }).strict();

// ------------------------------------------------------------------ Media
export const RmMediaStage = z.enum(['identified', 'warming', 'pitched', 'responded', 'placed', 'dormant']);
export type RmMediaStage = z.infer<typeof RmMediaStage>;
export const RmStoryStatus = z.enum(['pitched', 'nudged', 'replied', 'placed', 'dead', 'timedout']);
export const RmStory = z.object({
  id: Id, label: z.string(), exclusive: z.boolean(),
  /** Effective status: an open pitch older than 14 days reads "timedout" (computed on read, never stored by a GET). */
  status: RmStoryStatus, pitchedOn: IsoDate, lastTouch: IsoDate, daysSincePitch: z.number().int(), nudgeOpen: z.boolean(),
});
export const RmMediaLog = z.object({ id: Id, date: IsoDate, time: z.string().nullable(), kind: z.string(), channel: z.string(), dir: z.enum(['in', 'out']), summary: z.string(), story: z.string().nullable() });
export const RmMediaContact = z.object({
  id: Id, verticalKey: z.string(), name: z.string(), publication: z.string(), beat: z.string(), email: z.string(), linkedin: z.string(),
  stage: RmMediaStage, placedCount: z.number().int(), notes: z.string(), skipUntil: IsoDate.nullable(),
  stories: z.array(RmStory), lastLog: z.array(RmMediaLog), version: z.number().int().nonnegative(),
});
export type RmMediaContact = z.infer<typeof RmMediaContact>;
export const RmMediaListQuery = z.object({ vertical: VKey.optional() });
export const RmMediaDue = z.object({ contactId: Id, name: z.string(), publication: z.string(), kind: z.enum(['nudge', 'warm']), story: z.string().nullable(), text: z.string(), since: IsoDate });
export const RmMediaListResponse = z.object({
  items: z.array(RmMediaContact),
  placedThisQuarter: z.number().int(), quarterLabel: z.string(),
  /** At most two a day (the media doctrine). */
  due: z.array(RmMediaDue),
});
export const RmCreateMedia = z.object({ vertical: VKey, name: txt(200).min(1), publication: txt(200).min(1), beat: txt(200).default(''), email: txt(200).default(''), linkedin: txt(500).default('') }).strict();
export const RmUpdateMedia = z.object({
  name: txt(200).min(1).optional(), publication: txt(200).min(1).optional(), beat: txt(200).optional(), email: txt(200).optional(), linkedin: txt(500).optional(),
  stage: RmMediaStage.optional(), notes: txt(10_000).optional(), version: z.number().int().nonnegative(),
}).strict();
export const RmPitch = z.object({ story: txt(200).min(1), exclusive: z.boolean().default(false), overrideReason: txt(500).optional() }).strict();
export const RmStoryAction = z.object({ story: txt(200).min(1) }).strict();
export const RmMediaDebrief = z.object({ notes: z.string().trim().min(1).max(8000) }).strict();
export const RmMediaDebriefProposal = z.object({
  summary: z.string(), stageHint: RmMediaStage.nullable(),
  storyUpdates: z.array(z.object({ label: z.string(), status: z.enum(['pitched', 'replied', 'placed', 'dead']) })),
});
export const RmMediaDebriefResponse = z.object({ proposal: RmMediaDebriefProposal.nullable() });
export const RmMediaDebriefApply = z.object({ proposal: RmMediaDebriefProposal }).strict();

// ------------------------------------------------------------------ route table (documentation + tests)
export const RM_ROUTES: ReadonlyArray<readonly [method: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE', path: string]> = [
  ['GET', '/reach/people'], ['GET', '/reach/people/facets'], ['GET', '/reach/people/:id'], ['POST', '/reach/people'],
  ['PATCH', '/reach/people/:id'], ['POST', '/reach/people/:id/release-lock'], ['DELETE', '/reach/people/:id'],
  ['POST', '/reach/import/phonebook'], ['POST', '/reach/import/linkedin'], ['POST', '/reach/import/linkedin/add'],
  ['POST', '/reach/import/linkedin/fuzzy'], ['POST', '/reach/import/linkedin/apply'], ['POST', '/reach/import/tags'], ['POST', '/reach/icp'],
  ['POST', '/reach/tools/auto-tag'], ['POST', '/reach/tools/join-books'], ['GET', '/reach/tools/analyse'], ['GET', '/reach/tools/audit'],
  ['POST', '/reach/tools/exclusion-sweep'], ['GET', '/reach/check'], ['POST', '/reach/export'],
  ['GET', '/reach/settings'], ['PUT', '/reach/settings'],
  ['GET', '/reach/audiences'], ['POST', '/reach/audiences'], ['DELETE', '/reach/audiences/:id'],
  ['GET', '/reach/targets'], ['POST', '/reach/targets'], ['PATCH', '/reach/targets/:id'], ['DELETE', '/reach/targets/:id'],
  ['POST', '/reach/targets/seed'], ['POST', '/reach/targets/import'], ['POST', '/reach/targets/:id/officials'],
  ['POST', '/reach/targets/:id/paths'], ['PATCH', '/reach/target-paths/:id'], ['DELETE', '/reach/target-paths/:id'],
  ['POST', '/reach/targets/:id/propose-paths'], ['POST', '/reach/labels'], ['GET', '/reach/mailers'], ['POST', '/reach/mailers'],
  ['POST', '/reach/li/parse'], ['POST', '/reach/li/engagements'], ['GET', '/reach/li/authors'],
  ['POST', '/reach/li/engagements/:id/stage'], ['POST', '/reach/li/engagements/:id/close-connection'], ['POST', '/reach/li/engagements/:id/close-thread'],
  ['POST', '/reach/li/engagements/:id/reaction'], ['POST', '/reach/li/engagements/:id/dm/draft'], ['PUT', '/reach/li/engagements/:id/dm'],
  ['POST', '/reach/li/authors/:id/grant'], ['PUT', '/reach/li/authors/:id/link'],
  ['GET', '/media'], ['POST', '/media'], ['PATCH', '/media/:id'], ['DELETE', '/media/:id'],
  ['POST', '/media/:id/pitch'], ['POST', '/media/:id/nudge'], ['POST', '/media/:id/reply'], ['POST', '/media/:id/placed'],
  ['POST', '/media/:id/warm'], ['POST', '/media/:id/skip'], ['POST', '/media/:id/debrief'], ['POST', '/media/:id/debrief/apply'],
] as const;
